import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { UseFormReturn } from 'react-hook-form';

import { ICardTransactionFilterFormFields } from '@/store/types/card-transactions.type';
import {
  cardPaymentTypes,
  CardTransactionPaymentTypes,
  paymentTypes,
  TCardTransactionFormFilters,
  TPaymentType,
} from '@/features/cards/store/models/card.model';

import { ICheckField, ICheckOption } from '@/ui-kit/table-filters/filters.type';

export function mapCardTransactionPaymentTypes(value: TPaymentType[]): TCardTransactionFormFilters {
  const valueSet = new Set(value);
  const filterProps: TCardTransactionFormFilters = {};

  if (valueSet.has(CardTransactionPaymentTypes.Withdrawal)) {
    filterProps.paymentTypes = [CardTransactionPaymentTypes.Withdrawal];
  }

  const has3DS = valueSet.has(CardTransactionPaymentTypes.with3DS);
  const hasNo3DS = valueSet.has(CardTransactionPaymentTypes.without3DS);

  if (has3DS || hasNo3DS) {
    filterProps.paymentTypes = [...(filterProps.paymentTypes || []), CardTransactionPaymentTypes.Payment];
    filterProps.is3DS = has3DS && hasNo3DS ? undefined : has3DS ? 1 : 0;
  }

  if (valueSet.has(CardTransactionPaymentTypes.digitizedCardPayment)) {
    filterProps.xpay = 1;
  }

  return filterProps;
}

export function useCardTransactionPaymentTypeField(
  methods: UseFormReturn<ICardTransactionFilterFormFields>,
  onChange,
): ICheckField {
  const { t } = useTranslation();

  const paymentTypeWatch = methods.watch('paymentTypes');
  const isReset = paymentTypeWatch === undefined;

  const initialOptions = paymentTypes.map((type) => ({
    label: t(`pages.currentProfiles.cards.filters.payment.type.${type}`),
    value: type,
    isDisabled: false,
    children:
      type === 'Payment'
        ? cardPaymentTypes.map((cardPaymentType) => ({
            label: t(`pages.currentProfiles.cards.filters.payment.type.${cardPaymentType}`),
            value: cardPaymentType,
            isDisabled: false,
          }))
        : undefined,
  }));

  const [paymentTypeOptions, setPaymentTypeOptions] = useState<ICheckOption[]>();

  const handlePaymentTypeChange = useCallback(
    (value: ICardTransactionFilterFormFields['paymentTypes']) => {
      const hasWithdrawal = value?.includes('Withdrawal');
      const has3DSTypes = value?.some((v) => v === 'with3DS' || v === 'without3DS');

      setPaymentTypeOptions((prev) =>
        prev.map((option) => {
          if (option.value === 'Withdrawal') {
            return { ...option, isDisabled: has3DSTypes };
          }
          if (option.value === 'Payment') {
            return {
              ...option,
              isDisabled: hasWithdrawal,
              children: option.children?.map((child) => ({
                ...child,
                isDisabled: hasWithdrawal,
              })),
            };
          }
          return option;
        }),
      );

      methods.setValue('paymentTypes', value);
      onChange();
    },
    [onChange, methods],
  );

  useEffect(() => {
    if (isReset) {
      setPaymentTypeOptions(initialOptions);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isReset]);

  const paymentTypeValue = useMemo(() => {
    const typeFlatMap = initialOptions.flatMap((option) => (option?.children?.length > 0 ? option.children : option));

    return typeFlatMap.filter((option) => paymentTypeWatch?.includes(option.value as string));
  }, [initialOptions, paymentTypeWatch]);

  return {
    values: paymentTypeValue,
    handleChange: handlePaymentTypeChange,
    options: paymentTypeOptions,
  };
}
